import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { DateTime } from "luxon";
import { NGXLogger } from "ngx-logger";
import { ILineElement } from "src/app/shared/components/line-chart/interfaces/iline-element";
import { TourService } from "src/app/shared/services/tour.service";
import { UtilsService } from "src/app/shared/services/utils.service";
import { BarGraphElement, GlobalStats } from "../models/global-stats.model";
import { User } from "../models/user.model";
import { GlobalStatsService } from "../services/global-stats.service";
import { AppStore } from "./app.store";
import { AuthStore } from "./auth.store";
import { MeterStore } from "./meter.store";
import { ShowerheadStore } from "./showerhead.store";
import { UserStore } from "./user.store";

enum DashboardScreenMode {
  Normal = "normal",
  Loading = "loading",
}

const LOG_TAG = "[DASHBOARD-STORE] ";
@Injectable({
  providedIn: "root",
})
export class DashboardStore {
  private logger: NGXLogger = inject(NGXLogger);
  private userStore: UserStore = inject(UserStore);
  private utilsService: UtilsService = inject(UtilsService);
  private globalStatsService: GlobalStatsService = inject(GlobalStatsService);
  private appStore: AppStore = inject(AppStore);
  private authStore: AuthStore = inject(AuthStore);
  private meterStore: MeterStore = inject(MeterStore);
  private showerheadStore: ShowerheadStore = inject(ShowerheadStore);
  private tourService: TourService = inject(TourService);

  private screenMode: DashboardScreenMode = DashboardScreenMode.Normal;

  private readonly error = signal<string | null>(null);
  readonly error$ = this.error.asReadonly();

  // ---------------------------
  // --- page header
  // ---------------------------

  readonly headers$ = computed(() => {
    const headers = [];

    headers.push({
      id: "dashboard",
      label: $localize`:@@dashboard.header:Dashboard`,
      url: "/dashboard",
    });

    return headers;
  });

  // ---------------------------
  // --- touring
  // ---------------------------

  readonly touring = {
    firstStep: {
      url: "/dashboard",
      title: $localize`:@@dashboard.touring.firstStepTitle:Welcome to My Hydrao !`,
      text: $localize`:@@dashboard.touring.firstStepText:My HYDRAO is the place where you can manage all your showerhead and meter data. Let's take some time to walk you through the website.`,
    },
    menu: {
      url: "/dashboard",
      title: $localize`:@@dashboard.touring.menuTitle:Menu`,
      text: $localize`:@@dashboard.touring.menuText:You can navigate on each section from the main menu.`,
    },
    settingsStep: {
      url: "/settings",
      title: $localize`:@@dashboard.touring.settingsStepTitle:Settings section`,
      text: $localize`:@@dashboard.touring.settingsStepText:The settings section is where you can find anything concerning your account`,
    },
    exportData: {
      url: "/settings",
      title: $localize`:@@dashboard.touring.exportDataTitle:Export data`,
      text: $localize`:@@dashboard.touring.exportDataText:In the account section, you can export your data in an excel file from here.`,
    },
    menuHome: {
      url: "/dashboard",
      title: $localize`:@@dashboard.touring.menuHomeTitle:dashboard`,
      text: $localize`:@@dashboard.touring.menuHomeText:You can go to dashboard whenever you want by click on HYDRAO logo.`,
    },
    dashArea: {
      url: "/dashboard",
      title: $localize`:@@dashboard.touring.dashAreaTitle:global consumption`,
      text: $localize`:@@dashboard.touring.dashAreaText:In the dashboard, you can see here the global consumption of your devices.`,
    },
    dashRepartition: {
      url: "/dashboard",
      title: $localize`:@@dashboard.touring.dashRepartitionTitle:Repartition map`,
      text: $localize`:@@dashboard.touring.dashRepartitionText:You can see the water consumption repartition, on the last 24 hours or 30 days`,
    },
    dashAlerts: {
      url: "/dashboard",
      title: $localize`:@@dashboard.touring.dashAlertsTitle:last triggered alerts`,
      text: $localize`:@@dashboard.touring.dashAlertsText:You can see here the last triggeered alerts.`,
    },
    dashActive: {
      url: "/dashboard",
      title: $localize`:@@dashboard.touring.dashActiveTitle:devices`,
      text: $localize`:@@dashboard.touring.dashActiveText:You can see which devices were seen on the last day`,
    },
    continueTour: {
      url: "/dashboard",
      title: $localize`:@@dashboard.touring.continueTourTitle:Continue tour`,
      text: $localize`:@@dashboard.touring.continueTourText:You can continue the tour by clicking in one of the sections`,
    },
  };

  readonly tourSteps$ = computed(() => {
    const tourSteps = [];

    Object.entries(this.touring).forEach(([step, data]) => {
      tourSteps.push(step + "@" + data.url);
    });

    return tourSteps;
  });

  getTourTitle(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.title : "";
  }

  getTourText(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.text : "";
  }

  // ---------------------
  // --- user
  // ---------------------

  private statsLastFetch: Date;
  private statsRefreshDelay: number = 5; // 5 secondes

  private fetchStatsTimer: any;

  private readonly statsLoading = signal(false);
  readonly statsLoading$ = this.statsLoading.asReadonly();

  private readonly stats = signal<GlobalStats>(null);
  private tourStats = signal<GlobalStats>(null);

  readonly stats$ = computed(() => {
    const stats = this.stats();
    const tourStats = this.tourStats();
    const tourMode = this.appStore.tourMode$();

    return tourMode ? tourStats : stats;
  });

  // Fetch user data from an API
  fetchStats(user: User): void {
    if (this.statsLoading()) return;

    // check if refresh necessary
    if (
      !this.statsLastFetch ||
      this.utilsService.calculateElapsedSeconds(this.statsLastFetch) >
        this.statsRefreshDelay
    ) {
      this.statsLoading.set(true);
      this.error.set(null);
      console.log(LOG_TAG + "MIGRATION / fetchStats...");

      this.globalStatsService.getStatsForDashboardForStore(user).subscribe({
        next: (stats: GlobalStats) => {
          const fixedStats = this._fixStats(stats, user);
          console.log(LOG_TAG + "new stats", stats, fixedStats);
          this.stats.set(fixedStats);

          if (this.screenMode !== DashboardScreenMode.Loading) {
            this.statsLoading.set(false);
          }
          this.statsLastFetch = new Date();
        },
        error: (err) => {
          console.error(LOG_TAG + "failed to fetch stats : ", err);
          this.error.set(err.message || "An error occurred.");
          this.statsLoading.set(false);
        },
      });
    }
  }

  // ---------------------
  // --- LIFE CYCLE METHODS
  // ---------------------

  public constructor() {
    this._prepareTourData();
    effect(
      () => {
        const logged = this.authStore.logged$();
        const user = this.userStore.user$();
        const metersById = this.meterStore.metersById$();
        const showerheadById = this.showerheadStore.showerheadsById$();

        if (!logged || !user) return;

        console.log(LOG_TAG + "EFFECT => refresh stats from user & meters");

        if (this.fetchStatsTimer) clearTimeout(this.fetchStatsTimer);
        this.fetchStatsTimer = setTimeout(async () => {
          this.fetchStats(user);
          this.meterStore.fetchLast30dStats();
          this.showerheadStore.fetchLast30dStats();
        }, 500);
      },
      { allowSignalWrites: true }
    );
  }

  public clear() {
    this.statsLastFetch = null;
    if (this.fetchStatsTimer) clearTimeout(this.fetchStatsTimer);

    this.statsLoading.set(false);
    this.stats.set(null);

    this.error.set(null);
  }

  // ---- PRIVATE METHODS -------

  private _fixStats(stats: GlobalStats, user: User): GlobalStats {
    let globalConsumption: ILineElement[] = [];
    let globalConsumptionBar: BarGraphElement[] = [];

    let toDate: DateTime = DateTime.local().setZone(
      this.utilsService.getTimezone()
    );
    let fromDate = toDate.minus({ month: 1 }).startOf("day");

    if (stats.meter_details)
      stats.meter_details.map((d) =>
        d.date.setZone(this.utilsService.getTimezone()).toISO()
      );
    if (stats.sh_details)
      stats.sh_details.map((d) =>
        d.date.setZone(this.utilsService.getTimezone()).toISO()
      );

    const timezone = this.utilsService.getTimezone();
    const canViewSh = user.canViewSh();
    const canViewMeters = user.canViewMeters();

    for (
      let curDate = fromDate;
      curDate < toDate;
      curDate = curDate.plus({ day: 1 })
    ) {
      let elt: ILineElement = {
        date: curDate,
        line1Value: 0,
        line2Value: 0,
      };

      const compareDate = curDate.toMillis();
      let shVol = 0;
      let meterVol = 0;
      if (stats && stats.sh_details && canViewSh) {
        let statsElt = stats.sh_details.find(
          (d) => d.date.setZone(timezone).toMillis() == compareDate
        );
        if (statsElt) {
          shVol = statsElt.total_vol;
          elt.line1Value = statsElt.total_vol;
        }
      }

      if (stats && stats.meter_details && canViewMeters) {
        let statsElt = stats.meter_details.find(
          (d) => d.date.setZone(timezone).toMillis() == compareDate
        );

        if (statsElt) {
          meterVol = statsElt.total_vol;
          if (canViewSh) {
            elt.line2Value = statsElt.total_vol;
          } else {
            elt.line1Value = statsElt.total_vol;
          }
        }
      }

      this.logger.debug(
        `process ${curDate.toISO()} -> sh = ${elt.line1Value} m = ${
          elt.line2Value
        }`
      );

      let barElt = new BarGraphElement(elt.date, meterVol, shVol);

      globalConsumption.push(elt);
      globalConsumptionBar.push(barElt);
    }
    stats.lineChart = globalConsumption;
    stats.barChart = globalConsumptionBar;

    return stats;
  }

  private _prepareTourData() {
    this.tourService.initData();

    let stats = new GlobalStats(
      this.tourService.totalMeterVolMonth,
      this.tourService.totalMeterVolMonth,
      0,
      0,
      2,
      1,
      1,
      1,
      0,
      0,
      0,
      null,
      [],
      DateTime.local().minus({ hours: 1 }).toJSDate(),
      new Date()
    );

    stats.barChart = this.tourService.meterDataLast30d.map((d) => {
      const shVol = this.tourService.shDataLast30d.find((e) => e[0] == d[0])[1];
      let elt = new BarGraphElement(d[0], d[1], shVol);

      return elt;
    });

    stats.lineChart = this.tourService.meterDataLast30d.map((d) => {
      const shVol = this.tourService.shDataLast30d.find((e) => e[0] == d[0])[1];
      let elt: ILineElement = {
        date: d[0],
        line1Value: shVol,
        line2Value: d[1],
      };

      return elt;
    });

    this.tourStats.set(stats);
  }
}
